import { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { vendedoresService } from '@/services/vendedoresService';
import { isValidCPF, isValidCNPJ, formatCNPJ } from '@/utils/cpfValidation';
import { Loader2, Mail, Phone, User, FileText, DollarSign } from 'lucide-react';
import { EnderecoParceiroForm, type EnderecoParceiroValues } from '@/components/Parceiros/EnderecoParceiroForm';

// Funções de formatação
const formatTelefone = (value: string) => {
  value = value.replace(/\D/g, '').slice(0, 11);
  if (value.length === 11) {
    return value.replace(/^(\d{2})(\d{5})(\d{4})$/, '($1) $2-$3');
  } else if (value.length === 10) {
    return value.replace(/^(\d{2})(\d{4})(\d{4})$/, '($1) $2-$3');
  }
  return value;
};

const formatCPF = (value: string) => {
  value = value.replace(/\D/g, '').slice(0, 11);
  return value.replace(/^(\d{3})(\d{3})(\d{3})(\d{2})$/, '$1.$2.$3-$4');
};

const initialEndereco: EnderecoParceiroValues = {
  cep: '',
  endereco: '',
  numero: '',
  bairro: '',
  cidade: '',
  estado: '',
};

interface CadastrarVendedorProps {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

type TipoPessoa = 'PF' | 'PJ';

export function CadastrarVendedor({ open, onClose, onSuccess }: CadastrarVendedorProps) {
  const [tipoPessoa, setTipoPessoa] = useState<TipoPessoa>('PF');
  const [formData, setFormData] = useState({
    nome: '',
    email: '',
    telefone: '',
    cpf: '',
    pix: '',
    cnpj: '',
    razao_social: '',
    nome_fantasia: '',
    cpf_representante: ''
  });
  const [endereco, setEndereco] = useState<EnderecoParceiroValues>(initialEndereco);
  const [loading, setLoading] = useState(false);

  // Reset form quando o dialog abre/fecha
  useEffect(() => {
    if (!open) {
      setTipoPessoa('PF');
      setFormData({
        nome: '', email: '', telefone: '', cpf: '', pix: '',
        cnpj: '', razao_social: '', nome_fantasia: '', cpf_representante: ''
      });
      setEndereco(initialEndereco);
    }
  }, [open]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const camposEndereco: (keyof EnderecoParceiroValues)[] = ['cep', 'endereco', 'numero', 'bairro', 'cidade', 'estado'];
    const faltando = camposEndereco.filter((c) => !endereco[c]?.trim());
    if (faltando.length > 0) {
      toast.error('Preencha todos os campos do endereço (CEP, rua, número, bairro, cidade e estado).');
      return;
    }

    const nomeOuRazao = tipoPessoa === 'PJ' ? formData.razao_social?.trim() : formData.nome?.trim();
    if (!nomeOuRazao || !formData.email || !formData.telefone) {
      toast.error(tipoPessoa === 'PJ' ? 'Razão social, email e telefone são obrigatórios!' : 'Nome, email e telefone são obrigatórios!');
      return;
    }

    if (tipoPessoa === 'PF') {
      const cpfDigits = formData.cpf.replace(/\D/g, '');
      if (!cpfDigits || cpfDigits.length !== 11) {
        toast.error('CPF inválido.');
        return;
      }
      if (!isValidCPF(formData.cpf)) {
        toast.error('CPF inválido.');
        return;
      }
    } else {
      const cnpjDigits = formData.cnpj.replace(/\D/g, '');
      if (!cnpjDigits || cnpjDigits.length !== 14) {
        toast.error('CNPJ inválido.');
        return;
      }
      if (!isValidCNPJ(formData.cnpj)) {
        toast.error('CNPJ inválido.');
        return;
      }
      if (!formData.razao_social?.trim()) {
        toast.error('Razão social é obrigatória para Pessoa Jurídica.');
        return;
      }
      const cpfRepDigits = formData.cpf_representante.replace(/\D/g, '');
      if (!cpfRepDigits || cpfRepDigits.length !== 11) {
        toast.error('CPF do representante legal é obrigatório e inválido.');
        return;
      }
      if (!isValidCPF(formData.cpf_representante)) {
        toast.error('CPF do representante legal inválido.');
        return;
      }
    }

    setLoading(true);

    try {
      const payload: any = {
        nome: tipoPessoa === 'PJ' ? formData.razao_social?.trim() : formData.nome,
        email: formData.email,
        telefone: formData.telefone,
        pix: formData.pix || undefined,
        cidade: endereco.cidade.trim(),
        estado: endereco.estado.trim().toUpperCase(),
        cep: endereco.cep.replace(/\D/g, ''),
        endereco: endereco.endereco.trim(),
        numero: endereco.numero.trim(),
        bairro: endereco.bairro.trim(),
        tipo_pessoa: tipoPessoa
      };
      if (tipoPessoa === 'PF') {
        payload.cpf = formData.cpf.replace(/\D/g, '');
      } else {
        payload.cnpj = formData.cnpj.replace(/\D/g, '');
        payload.razao_social = formData.razao_social.trim();
        payload.nome_fantasia = formData.nome_fantasia?.trim() || undefined;
        payload.cpf_representante = formData.cpf_representante.replace(/\D/g, '');
      }

      const response = await vendedoresService.createVendedor(payload);

      toast.success('Vendedor cadastrado com sucesso!');
      setTipoPessoa('PF');
      setFormData({
        nome: '', email: '', telefone: '', cpf: '', pix: '',
        cnpj: '', razao_social: '', nome_fantasia: '', cpf_representante: ''
      });
      setEndereco(initialEndereco);
      onSuccess();
      onClose();
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Erro ao cadastrar vendedor');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading) {
      setTipoPessoa('PF');
      setFormData({
        nome: '', email: '', telefone: '', cpf: '', pix: '',
        cnpj: '', razao_social: '', nome_fantasia: '', cpf_representante: ''
      });
      setEndereco(initialEndereco);
      onClose();
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-[500px] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Cadastrar Novo Vendedor</DialogTitle>
        </DialogHeader>
        
        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Tipo: Pessoa Física ou Jurídica */}
          <div className="space-y-2">
            <Label>Tipo de cadastro</Label>
            <Select value={tipoPessoa} onValueChange={(v: TipoPessoa) => setTipoPessoa(v)}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="PF">Pessoa Física (CPF)</SelectItem>
                <SelectItem value="PJ">Pessoa Jurídica (CNPJ)</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Dados PJ: primeiros campos do formulário (Razão Social, CNPJ, Representante Legal, CPF) */}
          {tipoPessoa === 'PJ' && (
            <>
              <div className="space-y-2">
                <Label htmlFor="razao_social" className="flex items-center gap-2">
                  <FileText className="h-4 w-4" />
                  Razão Social *
                </Label>
                <Input
                  id="razao_social"
                  value={formData.razao_social}
                  onChange={(e) => setFormData({ ...formData, razao_social: e.target.value })}
                  placeholder="Ex: Solar Tech Ltda"
                  required
                  disabled={loading}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="cnpj">CNPJ *</Label>
                <Input
                  id="cnpj"
                  value={formatCNPJ(formData.cnpj)}
                  onChange={(e) => setFormData({ ...formData, cnpj: e.target.value.replace(/\D/g, '') })}
                  placeholder="00.000.000/0000-00"
                  required
                  disabled={loading}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="nome_fantasia">Representante Legal *</Label>
                <Input
                  id="nome_fantasia"
                  value={formData.nome_fantasia}
                  onChange={(e) => setFormData({ ...formData, nome_fantasia: e.target.value })}
                  placeholder="Nome do representante legal"
                  required
                  disabled={loading}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="cpf_representante">CPF do Representante Legal *</Label>
                <Input
                  id="cpf_representante"
                  value={formatCPF(formData.cpf_representante)}
                  onChange={(e) => setFormData({ ...formData, cpf_representante: e.target.value.replace(/\D/g, '') })}
                  placeholder="000.000.000-00"
                  required
                  disabled={loading}
                />
              </div>
            </>
          )}

          {/* Nome Completo (apenas PF) */}
          {tipoPessoa === 'PF' && (
            <div className="space-y-2">
              <Label htmlFor="nome" className="flex items-center gap-2">
                <User className="h-4 w-4" />
                Nome Completo *
              </Label>
              <Input
                id="nome"
                value={formData.nome}
                onChange={(e) => setFormData({ ...formData, nome: e.target.value })}
                placeholder="Ex: João Silva"
                required
                disabled={loading}
              />
            </div>
          )}

          {/* Email */}
          <div className="space-y-2">
            <Label htmlFor="email" className="flex items-center gap-2">
              <Mail className="h-4 w-4" />
              Email *
            </Label>
            <Input
              id="email"
              type="email"
              value={formData.email}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              placeholder="email@exemplo.com"
              required
              disabled={loading}
            />
            <p className="text-xs text-muted-foreground">
              Será usado para login no sistema
            </p>
          </div>

          {/* Telefone */}
          <div className="space-y-2">
            <Label htmlFor="telefone" className="flex items-center gap-2">
              <Phone className="h-4 w-4" />
              Telefone *
            </Label>
            <Input
              id="telefone"
              value={formatTelefone(formData.telefone)}
              onChange={(e) => setFormData({ ...formData, telefone: e.target.value.replace(/\D/g, '') })}
              placeholder="(00) 00000-0000"
              required
              disabled={loading}
            />
          </div>

          {/* Endereço completo com CEP */}
          <div>
            <h3 className="text-sm font-medium mb-2">Endereço *</h3>
            <EnderecoParceiroForm
              values={endereco}
              onChange={setEndereco}
              disabled={loading}
              idPrefix="vendedor-form"
            />
          </div>

          {/* Dados PF: CPF */}
          {tipoPessoa === 'PF' && (
            <div className="space-y-2">
              <Label htmlFor="cpf" className="flex items-center gap-2">
                <FileText className="h-4 w-4" />
                CPF *
              </Label>
              <Input
                id="cpf"
                value={formatCPF(formData.cpf)}
                onChange={(e) => setFormData({ ...formData, cpf: e.target.value.replace(/\D/g, '') })}
                placeholder="000.000.000-00"
                disabled={loading}
                required
              />
            </div>
          )}

          {/* PIX (Opcional) */}
          <div className="space-y-2">
            <Label htmlFor="pix" className="flex items-center gap-2">
              <DollarSign className="h-4 w-4" />
              Chave PIX
            </Label>
            <Input
              id="pix"
              value={formData.pix}
              onChange={(e) => setFormData({ ...formData, pix: e.target.value })}
              placeholder="Ex: email@exemplo.com, CPF, telefone ou chave aleatória"
              disabled={loading}
            />
            <p className="text-xs text-muted-foreground">
              Campo opcional - Chave PIX para recebimento de comissões
            </p>
          </div>

          {/* Nota informativa */}
          <div className="bg-blue-50 border border-blue-200 rounded-md p-3">
            <p className="text-xs text-blue-800">
              <strong>Nota:</strong> Uma senha provisória padrão (<strong>Sun7energy</strong>) será configurada para o vendedor.
              No primeiro acesso, o vendedor será redirecionado automaticamente para definir uma senha definitiva.
            </p>
          </div>

          {/* Botões */}
          <div className="flex justify-end gap-2 pt-4">
            <Button 
              type="button" 
              variant="outline" 
              onClick={handleClose}
              disabled={loading}
            >
              Cancelar
            </Button>
            <Button type="submit" disabled={loading}>
              {loading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Cadastrando...
                </>
              ) : (
                'Cadastrar Vendedor'
              )}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}

